import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppModule } from '../app.module';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { NotificationService } from '../shared/notification.service';
import { trigger, transition, style, animate } from '@angular/animations';

@Component({
  selector: 'app-login',
  imports: [AppModule],
  templateUrl: './login.html',
  styleUrl: './login.less',
  animations: [
    trigger('slideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-30px) scale(0.95)' }),
        animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
      ])
    ])
  ]
})
export class Login implements OnInit, OnDestroy {

  private returnUrl: string = '/home';
  private countdownInterval: any;

  public loginForm: FormGroup = new FormGroup({
    username: new FormControl(''),
    password: new FormControl(''),
    otp: new FormControl(''),
    remember: new FormControl(false)
  });

  public showPasswordField: boolean = false;
  public isCellNumber: boolean = false;
  public otpRequested: boolean = false;
  public otpCountdown: number = 0;
  public isRequestingOtp: boolean = false;

  constructor(private router: Router, private authService: AuthService, private notification: NotificationService, private route: ActivatedRoute) { }
  
  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
    
    // Watch username field changes
    this.loginForm.get('username')?.valueChanges.subscribe(value => {
      this.onUsernameChange(value);
    });
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private onUsernameChange(value: string): void {
    // Show password field if username has value
    this.showPasswordField = !!value && value.trim().length > 0;
    
    // Check if it's a cell number (starts with 0, length 10, all numeric)
    this.isCellNumber = this.validateCellNumber(value);
    
    // Reset OTP state when username changes
    if (this.otpRequested) {
      this.otpRequested = false;
      this.otpCountdown = 0;
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
    }
  }

  private validateCellNumber(value: string): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    return trimmed.length === 10 && trimmed.startsWith('0') && /^\d+$/.test(trimmed);
  }

  public sendOtp(): void {
    const username = this.loginForm.value.username;
    if (!username) return;

    this.isRequestingOtp = true;
    this.authService.sendOtp(username).subscribe({
      next: (response) => {
        this.isRequestingOtp = false;
        this.notification.showSuccess(response.detail || 'OTP sent successfully');
        this.otpRequested = true;
        this.startCountdown();
      },
      error: (error) => {
        this.isRequestingOtp = false;
        this.notification.handleError(error, 'Failed to send OTP');
      }
    });
  }

  private startCountdown(): void {
    this.otpCountdown = 60;
    this.countdownInterval = setInterval(() => {
      this.otpCountdown--;
      if (this.otpCountdown <= 0) {
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  public get canRequestOtp(): boolean {
    return this.isCellNumber && this.otpCountdown === 0 && !this.isRequestingOtp;
  }

  public submitLoginForm(): void {
    const username = this.loginForm.value.username;
    
    if (this.otpRequested) {
      // Login with OTP
      const otp = this.loginForm.value.otp;
      if (!otp) {
        this.notification.showWarning('Please enter the OTP');
        return;
      }
      
      this.authService.loginWithOtp(username, otp).subscribe({
        next: () => {
          console.log('Login with OTP successful');
          this.router.navigate([this.returnUrl]);
        },
        error: error => {
          this.notification.handleError(error, "Login failed");
        }
      });
    } else {
      // Regular password login
      const password = this.loginForm.value.password;
      if (!password) {
        this.notification.showWarning('Please enter your password');
        return;
      }
      
      this.authService.login(username, password).subscribe({
        next: () => {
          console.log('Login successful:', this.loginForm.value);
          console.log("Returning to route", this.returnUrl);
          this.router.navigate([this.returnUrl]);
        },
        error: error => {
          this.notification.handleError(error, "Login failed");
        }
      });
    }
  }
}
